import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localizeSystemText } from '../../i18n/messages';
import { PORTAL_SECTIONS, portalSearch } from './clientPortalNavigation';
import { fullName } from './clientPortalSelectors';
import LanguageSwitcher from '../common/LanguageSwitcher';

const sections = [...PORTAL_SECTIONS, ['school-calendar', 'Kalendarz szkoły'], ['support', 'Moje zgłoszenia']];

export default function ClientPortalShell({ section, students, selectedStudentId, scheduleWindow, busy, identity, canSwitch, onSwitch, onSignOut, onNavigate, onStudent, headingRef, children }) {
  const { t: tr } = useTranslation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const accountButton = useRef(null);
  const moreButton = useRef(null);
  const sectionLabel = section === 'support' ? tr('manage:integratedText37') : sections.find(([id]) => id === section)[1];
  const closeAccount = event => {
    if (event.key === 'Escape') { setAccountOpen(false); accountButton.current.focus(); }
  };
  const closeMore = event => {
    if (event.key === 'Escape' && moreOpen) { setMoreOpen(false); moreButton.current.focus(); }
  };
  const navLink = (id, label, className) => (
    <Link
      key={id}
      className={className}
      to={['school-calendar', 'support'].includes(id) ? `/client/${id}` : `/client${portalSearch(id, selectedStudentId, scheduleWindow)}`}
      aria-current={section === id ? 'page' : undefined}
      onKeyDown={closeMore}
      onClick={() => { setMoreOpen(false); onNavigate(id); }}
    >{id === 'support' ? tr('manage:integratedText37') : localizeSystemText(label, tr)}
    </Link>
  );
  return (
    <div className={`client-portal${section === 'schedule' ? ' client-portal--schedule' : ''}`}>
      <header className="client-portal__header fixed-top">
        <Link className="client-portal__brand" to="/">Extra English <span>{tr("client:clientPortal_44")}</span></Link>
        <div className="client-portal__header-actions">
          <LanguageSwitcher />
          <Link to="/" className="client-portal__school-link">{tr("client:schoolWebsite_45")}</Link>
          <div
            className="client-portal__account"
          >
            <button ref={accountButton} onKeyDown={closeAccount} type="button" aria-expanded={accountOpen} aria-controls="portal-account" onClick={() => setAccountOpen(!accountOpen)}>{tr("manage:account_276")}</button>
            {accountOpen && (
            <div id="portal-account" className="client-portal__account-panel">
              <div>{identity}</div>
              {canSwitch && <button type="button" disabled={busy} onKeyDown={closeAccount} onClick={onSwitch}>{tr("common:switchAccount")}</button>}
              <button type="button" onKeyDown={closeAccount} onClick={onSignOut}>{tr("common:signOut")}</button>
            </div>
            )}
          </div>
        </div>
      </header>
      <main className="fixed-header-page client-portal__layout">
        <nav className="client-portal__sidebar" aria-label={tr("client:portalSections_46")}>{sections.map(([id, label]) => navLink(id, label))}</nav>
        <div className="client-portal__content">
          <nav
            className="client-portal__mobile-nav"
            aria-label={tr("client:mobileNavigation_47")}
          >
            <div className="client-portal__mobile-tabs">
              {navLink('dashboard', tr("common:dashboard"))}{navLink('schedule', tr("client:lessons_33"))}
              <button ref={moreButton} onKeyDown={closeMore} type="button" aria-expanded={moreOpen} aria-controls="portal-more" onClick={() => setMoreOpen(!moreOpen)}>{tr("client:more_48")}</button>
            </div>
            {moreOpen && <div id="portal-more" className="client-portal__more">{sections.slice(2).map(([id, label]) => navLink(id, label))}</div>}
          </nav>
          <div className="client-portal__page-title"><h1 ref={headingRef} tabIndex="-1">{section === 'dashboard' ? tr("client:yourDashboard_49") : localizeSystemText(sectionLabel, tr)}</h1>
            {section !== 'dashboard' && navLink('dashboard', tr("client:backToDashboard_50"), 'btn manage-teal-button')}
          </div>
          {section !== 'billing' && students.length === 1 && <p className="client-portal__student-name">{tr("client:student_51")} <strong>{fullName(students[0])}</strong></p>}
          {section !== 'billing' && students.length > 1 && (
          <div className="client-portal__student-selector">
            <label htmlFor="portal-student">
              {tr("common:students")}
              <select id="portal-student" className="form-control" aria-label={tr('common:students')} value={selectedStudentId || ''} disabled={busy} onChange={event => onStudent(event.target.value || null)}>
                <option value="">{tr("client:allStudents_52")}</option>{students.map(student => <option key={student.id} value={student.id}>{fullName(student)}</option>)}
              </select>
            </label>
          </div>
          )}
          {section !== 'billing' && <span className="sr-only" role="status">{selectedStudentId ? tr('client:selectedStudent', { name: fullName(students.find(student => student.id === selectedStudentId)) }) : tr("client:allStudents_52")}</span>}
          {children}
        </div>
      </main>
    </div>
  );
}
ClientPortalShell.propTypes = {
  section: PropTypes.string.isRequired,
  students: PropTypes.array.isRequired,
  selectedStudentId: PropTypes.string,
  scheduleWindow: PropTypes.shape({ start: PropTypes.string.isRequired, days: PropTypes.number.isRequired }),
  busy: PropTypes.bool.isRequired,
  identity: PropTypes.string.isRequired,
  canSwitch: PropTypes.bool.isRequired,
  onSwitch: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onStudent: PropTypes.func.isRequired,
  headingRef: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};
ClientPortalShell.defaultProps = { selectedStudentId: null, scheduleWindow: null };
